import React, { useState, useCallback, useRef, useEffect } from "react";
import Cropper from "react-easy-crop";
import { useLazyQuery, useMutation } from "@apollo/client";
import styled from "styled-components";
import { Flex } from "rebass/styled-components";
import { RiImageAddLine } from "react-icons/ri";
import Resizer from "react-image-file-resizer";

import { Button } from "../../../../components/atoms/form/button";
import { SliderSelect } from "../../../../components/atoms/slider/slider";
import { Error } from "../../../../components/atoms/form/error";
import { FloatingContainer } from "../../../../components/wrappers/floatingContainer";
import { Body, Headline } from "../../../../components/text";
import { analytics } from "MVVM/Analytics";
import { canUploadGifs } from "MVVM/Utilities/ProfileBackground";
import { buildMediaUrl, uploadFileToS3 } from "MVVM/Utilities/MediaUpload";
import { AVATAR_UPLOAD_URL } from "MVVM/GraphQL/avatar";
import { USER_UPDATE } from "MVVM/GraphQL/user";

const ANIMATED_MIME_TYPES = ["image/gif", "image/webp"];
const UPLOAD_TYPE_BY_MIME = { "image/gif": "GIF", "image/webp": "WEBP" };
const Label = styled.label`
  background-color: ${(props) => props.theme.colors.ned};
  color: ${(props) => props.theme.colors.ice};
  border-color: ${(props) => props.theme.colors.navy};
  border-style: solid;
  border-width: 2px;
  padding-left: 26px;
  padding-right: 26px;
  padding-top: 24px;
  padding-bottom: 24px;
  text-decoration: none;
  margin-bottom: 20px;
  cursor: pointer;
  border-radius: 999px;

  input[type="file"] {
    display: none;
  }
  &:hover {
    background-color: ${(props) => props.theme.colors.darkerNed};
    border-color: ${(props) => props.theme.colors.envy};
  }
`;

const CropContainer = styled.div`
  position: relative;
  width: 100%;
  height: 250px;
  background: #333;
  margin-bottom: 20px;
`;

const StyledPhoto = styled(RiImageAddLine)`
  position: relative;
  color: ${(props) => props.theme.colors.envy};
`;

const AnimatedPreview = styled.img`
  border-radius: 999px;
  display: block;
  height: 250px;
  margin: 0 auto 12px;
  object-fit: cover;
  width: 250px;
`;

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
function rotateSize(width, height, rotation = 0) {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

export default function AvatarUpload({
  setModalOpen,
  allstarUser,
  getFreshProfile,
  skipProfileUpdate,
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [test] = useState();
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const [upImg, setUpImg] = useState({ file: undefined, url: undefined });
  const previewCanvasRef = useRef(null);
  const [state, setState] = useState({
    status: "pending",
    error: null,
    data: null,
  });

  const MIN_AVATAR_SIZE = 1_000; // 1KB
  const MAX_AVATAR_SIZE = 30_000_000; // 30MB

  const canUploadAnimated = canUploadGifs(allstarUser?.user?.userGroup?.type);
  const acceptedTypes = canUploadAnimated
    ? "image/png,image/jpeg,image/gif,image/webp"
    : "image/png,image/jpeg";
  const isAnimated =
    canUploadAnimated &&
    !!upImg.file &&
    ANIMATED_MIME_TYPES.includes(upImg.file.type);

  const [fetchAvatarUploadUrl] = useLazyQuery(AVATAR_UPLOAD_URL, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });
  const [updateUser] = useMutation(USER_UPDATE);

  const onSelectFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (state.error) {
      setState((_state) => ({ ..._state, error: null }));
    }

    if (!(file.size >= MIN_AVATAR_SIZE && file.size <= MAX_AVATAR_SIZE)) {
      setState({
        status: "pending",
        error: {
          msg: `Your image is too ${
            file.size < MIN_AVATAR_SIZE
              ? "small, make sure it's above 1kB"
              : "large, make sure it's under 30MB"
          }`,
        },
      });
      setUpImg({});
      return;
    }

    URL.revokeObjectURL(upImg.url);
    setUpImg({
      file,
      url: URL.createObjectURL(file),
    });
  };

  useEffect(() => {
    return () => URL.revokeObjectURL(upImg.url);
  }, [upImg.url]);

  const resizeImage = async (blob, extension) =>
    new Promise((resolve) =>
      Resizer.imageFileResizer(
        new File([blob], `avatar.${extension}`),
        400,
        400,
        extension,
        80,
        0,
        (file) => {
          resolve(file);
        },
        "file",
        // 200,
        // 200
      ),
    );

  const persistAvatar = async (file, type, { animated }) => {
    try {
      const { data, error } = await fetchAvatarUploadUrl({
        variables: { type },
      });
      const presigned = data?.me?.mediaUploadUrl;

      if (error || !presigned) {
        const isUnauthorized = error?.graphQLErrors?.some(
          (gqlError) =>
            gqlError.extensions?.code === "UNAUTHORIZED" ||
            /unauthorized/i.test(gqlError.message),
        );
        setState({
          status: "error",
          error: {
            msg: isUnauthorized
              ? "Animated avatars require a paid subscription"
              : "We couldn't start your upload. Please try again.",
          },
          data: null,
        });
        return;
      }

      await uploadFileToS3(presigned, file);
      const url = buildMediaUrl(presigned);

      const { data: updateData } = await updateUser({
        variables: { input: { avatarUrl: url } },
      });

      if (updateData?.userUpdate?.errors?.length) {
        setState({
          status: "error",
          error: { msg: "We couldn't save your avatar. Please try again." },
          data: null,
        });
        return;
      }

      if (!skipProfileUpdate) getFreshProfile();
      setState({ status: "success", error: null, data: null });
      setModalOpen(false);
      analytics.track("Avatar Uploaded", {
        user: allstarUser?.user?._id,
        size: file.size,
        animated,
      });
    } catch (e) {
      setState({
        status: "error",
        error: { msg: "Upload failed. Please try again." },
        data: null,
      });
    }
  };

  const handleApply = () => (isAnimated ? uploadAnimated() : uploadBlob());

  async function uploadAnimated() {
    if (!upImg.file) return;

    const type = UPLOAD_TYPE_BY_MIME[upImg.file.type];
    setState({ status: "loading", error: null, data: null });
    await persistAvatar(upImg.file, type, { animated: true });
  }

  async function uploadBlob() {
    const canvas = previewCanvasRef.current;
    const image = await createImage(upImg.url);
    const ctx = canvas.getContext("2d");

    if (!canvas || !croppedAreaPixels) return;
    const extension = "png";
    setState({ status: "loading", error: null, data: null });

    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
      image.width,
      image.height,
    );

    const flip = { horizontal: false, vertical: false };
    canvas.width = bBoxWidth;
    canvas.height = bBoxHeight;

    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
    ctx.translate(-image.width / 2, -image.height / 2);
    ctx.drawImage(image, 0, 0);

    const data = ctx.getImageData(
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
    );

    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;

    ctx.putImageData(data, 0, 0);

    canvas.toBlob(
      async (blob) => {
        const file = await resizeImage(blob, extension);
        await persistAvatar(file, "PNG", { animated: false });
      },
      `image/${extension}`,
      1,
    );
  }

  return (
    <>
      <FloatingContainer
        flexDirection="column"
        alignItems="stretch"
        justifyContent="center"
        maxWidth="525px"
      >
        <>
          <Flex className="App" flexDirection="column">
            <Flex alignItems="center">
              <Label>
                <StyledPhoto
                  size={32}
                  title="Select Image"
                  name="select-image-button"
                />
                <input
                  type="file"
                  accept={acceptedTypes}
                  onChange={onSelectFile}
                />
              </Label>
              <Flex flexDirection="column" ml={6} mb={4}>
                <Headline color="ice" mb={2}>
                  Change Profile Picture
                </Headline>
                <Body color="chalk">
                  {canUploadAnimated ? "JPG, PNG, GIF, WebP" : "JPG, PNG"}
                </Body>
              </Flex>
            </Flex>
            {upImg.url && isAnimated && (
              <>
                <AnimatedPreview
                  src={upImg.url}
                  alt="Animated avatar preview"
                />
                <Body color="chalk" mb={4} textAlign="center">
                  Animated avatars are uploaded as-is and can&apos;t be cropped.
                </Body>
              </>
            )}
            {upImg.url && !isAnimated && (
              <>
                <CropContainer>
                  <Cropper
                    image={upImg.url}
                    crop={crop}
                    zoom={zoom}
                    aspect={1 / 1}
                    onCropChange={setCrop}
                    showGrid={false}
                    cropShape="round"
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                </CropContainer>
                <SliderSelect
                  width="100%"
                  padding={{ pb: "30px" }}
                  min={1}
                  max={3}
                  step={0.2}
                  onChange={(val) => {
                    setZoom(val);
                  }}
                  value={zoom}
                />
              </>
            )}

            {state.error && (
              <Error>{state.error.msg ?? "An error occured"}</Error>
            )}
            <canvas hidden ref={previewCanvasRef} />
            {test && <img src={test} alt="" />}

            <Flex alignItems="center" mt={7} justifyContent="flex-end">
              <Button
                classification="subtle"
                size="small"
                flex
                onClick={() => setModalOpen()}
              >
                Cancel
              </Button>
              <Button
                classification="main"
                flex
                size="small"
                ml={5}
                disabled={!upImg.url || state.status === "loading"}
                onClick={handleApply}
                name="avatar-apply-button"
              >
                {!upImg.url ? (
                  <>Select image to apply</>
                ) : (
                  <>{state.status === "loading" ? "Saving..." : "Apply"}</>
                )}
              </Button>
            </Flex>
          </Flex>
        </>
      </FloatingContainer>
    </>
  );
}
